export type AttendanceInputSuggestionIcon =
  | 'calendar'
  | 'students'
  | 'subject'
  | 'support'
  | 'ticket'

export type AttendanceInputSuggestion = {
  id: string
  label: string
  text: string
  icon?: AttendanceInputSuggestionIcon
  /** An action chip: runs this instead of sending `text` (e.g. "Create a support ticket"). */
  onSelect?: () => void
}

export type AttendanceAssistiveUiState = {
  showAttendanceDateRange: boolean
  showAttendanceDatePicker: boolean
  hasDateSelectionPayload: boolean
  showSchoolSelect: boolean
  showSchoolMultiSelect: boolean
  schoolMultiSelectOptionCount: number
  showMonthYearInput: boolean
  showSkipButton: boolean
  showStudentMultiSelect: boolean
  studentCandidateCount: number
  showClassScopePicker: boolean
  showClassDisambiguation: boolean
  showOptionChips: boolean
  optionChipsCount: number
}

/** True when any assistive picker/control is shown above the free-text input. */
export function isAttendanceAssistiveUiActive(state: AttendanceAssistiveUiState): boolean {
  return (
    state.showAttendanceDateRange ||
    (state.showAttendanceDatePicker && state.hasDateSelectionPayload) ||
    state.showSchoolSelect ||
    (state.showSchoolMultiSelect && state.schoolMultiSelectOptionCount > 0) ||
    state.showMonthYearInput ||
    state.showSkipButton ||
    (state.showStudentMultiSelect && state.studentCandidateCount > 0) ||
    state.showClassScopePicker ||
    state.showClassDisambiguation ||
    (state.showOptionChips && state.optionChipsCount > 0)
  )
}

/**
 * Do the attendance follow-up pills fit the answer on screen?
 *
 * Reported: after "how do I raise a support ticket?" and a clarification menu, the composer still
 * offered "Recent attendance / Most absences" — pills about a board the last answer did not come
 * from. They follow an attendance answer only; a clarification carries its own options, and a
 * manual, walkthrough or directory answer is not a place to suggest attendance figures. With no
 * attribution at all (a legacy or portal-composed message) they keep showing, as before.
 *
 * @param lastBotMessage the latest bot message, if any
 * @param lastCapability the capability the backend attributed the latest turn to, if known
 */
export function attendancePillsFitLastTurn(
  lastBotMessage: {clarification?: unknown; source?: string} | undefined,
  lastCapability?: string | null
): boolean {
  if (lastBotMessage?.clarification) return false
  if (lastCapability) return lastCapability.startsWith('attendance.')
  return lastBotMessage?.source !== 'faq'
}

/**
 * The grade name with the word that says WHICH DIMENSION it is.
 *
 * The examples already carry the school's own grade ("PRE-MED-I (Boys) (HSSC)"), and without
 * this they read "What is PRE-MED-I (Boys) (HSSC)'s attendance today?" — a question the backend
 * cannot resolve. Grade and section names are not guessable from their shape: the resolver looks
 * for a stored grade name with "grade" beside it, precisely because a bare "AS" or "2" is also a
 * class code, a year and an ordinary English word. The suggestion is the one prompt we compose
 * ourselves, so it is the one that should be phrased the way the resolver reads.
 *
 * The word is English in every locale on purpose: it is a token the backend matches on, not
 * prose. Names that already carry it ("Grade 5", "Class 2") are left alone.
 */
function asGradePhrase(gradeName?: string): string | undefined {
  const text = String(gradeName ?? '').trim()
  if (!text) return undefined
  return /\b(grade|class|jamat)\b/i.test(text) ? text : `grade ${text}`
}

/**
 * How far back the examples look, and the window the first example ASKS about.
 *
 * One number for both on purpose. It used to be two: the sample lookup verified a grade had
 * been marked at some point in the last ninety days, and then composed a question about TODAY.
 * A grade marked once, three days ago, passed that check and still answered "no attendance has
 * been recorded" — the suggestion we invited the user to click was the one question its own
 * evidence did not support. Verifying over exactly the window the question asks about is what
 * keeps the example answerable; if you change this, both halves move together.
 */
export const ATTENDANCE_SAMPLE_WINDOW_DAYS = 30

/** Real grade/subject names for the examples; omitted keys fall back to grade-free wording. */
export type AttendanceSuggestionSampleNames = {
  gradeName?: string
  subjectName?: string
}

/**
 * Starter examples for the attendance assistant. The welcome cards show `text` (the full
 * question that gets sent); the compact pills above the input show the shorter `label`.
 *
 * The examples are built from the school's own active grades and subjects — a college has no
 * "Grade 7", so a hardcoded example would prompt the user into a question that cannot be
 * answered. When those names are not available yet we drop the placeholder entirely and use
 * the `_GENERIC` wording instead of guessing.
 */
export function getWelcomeAttendanceSuggestions(
  t: (id: string, values?: Record<string, string>) => string,
  samples: AttendanceSuggestionSampleNames = {},
  options: {isStudent?: boolean} = {}
): AttendanceInputSuggestion[] {
  const {subjectName} = samples
  // Named as a grade, so the assistant resolves it instead of reading it as a class name.
  const gradeName = asGradePhrase(samples.gradeName)
  const days = String(ATTENDANCE_SAMPLE_WINDOW_DAYS)

  // A student can only read their own marks, so class rankings and class figures come back empty.
  if (options.isStudent) {
    return [
      {
        id: 'my_recent',
        label: t('CHATBOT.ATTENDANCE.SUGGEST_PILL_MY_RECENT'),
        text: t('CHATBOT.ATTENDANCE.SUGGEST_TEXT_MY_RECENT', {days}),
        icon: 'calendar',
      },
      {
        id: 'my_absences',
        label: t('CHATBOT.ATTENDANCE.SUGGEST_PILL_MY_ABSENCES'),
        text: t('CHATBOT.ATTENDANCE.SUGGEST_TEXT_MY_ABSENCES'),
        icon: 'students',
      },
    ]
  }

  const suggestions: AttendanceInputSuggestion[] = [
    {
      // A window, not "today". Attendance is not marked every day in every school — this tenant
      // has nothing at all on most dates — so a today-question is empty far more often than it
      // is wrong, and an empty first example reads as a broken assistant.
      id: 'class_recent',
      label: t('CHATBOT.ATTENDANCE.SUGGEST_PILL_CLASS_RECENT'),
      text: gradeName
        ? t('CHATBOT.ATTENDANCE.SUGGEST_TEXT_CLASS_RECENT', {grade: gradeName, days})
        : t('CHATBOT.ATTENDANCE.SUGGEST_TEXT_CLASS_RECENT_GENERIC', {days}),
      icon: 'calendar',
    },
    {
      id: 'top_absences',
      label: t('CHATBOT.ATTENDANCE.SUGGEST_PILL_TOP_ABSENCES'),
      text: gradeName
        ? t('CHATBOT.ATTENDANCE.SUGGEST_TEXT_TOP_ABSENCES', {grade: gradeName})
        : t('CHATBOT.ATTENDANCE.SUGGEST_TEXT_TOP_ABSENCES_GENERIC'),
      icon: 'students',
    },
  ]
  // No subject sample means the school records no subject attendance; the generic question would come back empty.
  if (!subjectName) return suggestions
  return [
    ...suggestions,
    {
      // Grounded in the SAME subject-wise summary the sample lookup used to pick `subjectName`
      // in the first place (see useAttendanceSuggestionSamples) — a "who teaches" question asks
      // about teacher assignments, a dataset that lookup never checks, so a subject+grade pair
      // with attendance marked can still have no teacher on record and the suggestion fails
      // ("No teachers are recorded as teaching English to Grade 2"). Asking about attendance
      // instead means the suggestion can only ever point at data we already confirmed exists.
      id: 'subject_attendance',
      label: t('CHATBOT.ATTENDANCE.SUGGEST_PILL_SUBJECT_ATTENDANCE'),
      text: gradeName
        ? t('CHATBOT.ATTENDANCE.SUGGEST_TEXT_SUBJECT_ATTENDANCE', {
            grade: gradeName,
            subject: subjectName,
            days,
          })
        : t('CHATBOT.ATTENDANCE.SUGGEST_TEXT_SUBJECT_ATTENDANCE_GENERIC', {days}),
      icon: 'subject',
    },
  ]
}
